import { useState } from 'react';
import { Database, Image, Download, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { ui } from '../../utils/ui';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

const triggerDownload = async (endpoint, filename, setLoading) => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Download failed.');
    }

    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded.`);
  } catch (err) {
    toast.error(err.message || 'Download failed.');
  } finally {
    setLoading(false);
  }
};

const today = () => new Date().toISOString().slice(0, 10);

const Backup = () => {
  const [dbLoading,  setDbLoading]  = useState(false);
  const [imgLoading, setImgLoading] = useState(false);

  return (
    <div style={{ padding: '32px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
          Backup
        </h1>
        <p style={{ fontSize: 15, color: '#6b7280', margin: 0 }}>
          Download a snapshot of your database and uploaded images.
        </p>
      </div>

      {/* Notice */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px', borderRadius: 12, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 28 }}>
        <ShieldCheck size={20} color="#2563eb" style={{ flexShrink: 0, marginTop: 2 }} />
        <p style={{ margin: 0, fontSize: 13, color: '#1e40af', lineHeight: 1.6 }}>
          Backups are generated live. Store them in a safe location. The DB backup contains all tables and data. The image backup is a ZIP of all uploaded files.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
        {/* DB Backup Card */}
        <div style={{ ...ui.card, padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Database size={24} color="#2563eb" />
            </div>
            <div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
                Database Backup
              </h2>
              <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Full PostgreSQL dump (.sql)</p>
            </div>
          </div>

          <div style={{ padding: '12px 14px', borderRadius: 10, backgroundColor: '#f8fafc', border: '1px solid #e5e7eb', marginBottom: 20 }}>
            {[
              ['Format',   'SQL dump (pg_dump)'],
              ['Contains', 'All tables, data, indexes'],
              ['Filename', `burans_hikes_db_${today()}.sql`],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: k !== 'Filename' ? '1px solid #f0f0f0' : 'none', fontSize: 13 }}>
                <span style={{ color: '#6b7280', fontWeight: 600 }}>{k}</span>
                <span style={{ color: '#111827', fontWeight: 700, textAlign: 'right' }}>{v}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => triggerDownload('/backup/db', `burans_hikes_db_${today()}.sql`, setDbLoading)}
            disabled={dbLoading}
            style={{
              width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
              backgroundColor: dbLoading ? '#93c5fd' : '#2563eb', color: '#ffffff',
              fontSize: 14, fontWeight: 700, cursor: dbLoading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background-color 0.15s',
            }}
          >
            {dbLoading ? (
              <>
                <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                Generating…
              </>
            ) : (
              <><Download size={16} /> Download DB Backup</>
            )}
          </button>
        </div>

        {/* Images Backup Card */}
        <div style={{ ...ui.card, padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Image size={24} color="#16a34a" />
            </div>
            <div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
                Images Backup
              </h2>
              <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>All uploaded files (.zip)</p>
            </div>
          </div>

          <div style={{ padding: '12px 14px', borderRadius: 10, backgroundColor: '#f8fafc', border: '1px solid #e5e7eb', marginBottom: 20 }}>
            {[
              ['Format',   'ZIP archive'],
              ['Contains', 'Room images, gallery files'],
              ['Filename', `burans_hikes_images_${today()}.zip`],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: k !== 'Filename' ? '1px solid #f0f0f0' : 'none', fontSize: 13 }}>
                <span style={{ color: '#6b7280', fontWeight: 600 }}>{k}</span>
                <span style={{ color: '#111827', fontWeight: 700, textAlign: 'right' }}>{v}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => triggerDownload('/backup/images', `burans_hikes_images_${today()}.zip`, setImgLoading)}
            disabled={imgLoading}
            style={{
              width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
              backgroundColor: imgLoading ? '#86efac' : '#16a34a', color: '#ffffff',
              fontSize: 14, fontWeight: 700, cursor: imgLoading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background-color 0.15s',
            }}
          >
            {imgLoading ? (
              <>
                <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                Zipping…
              </>
            ) : (
              <><Download size={16} /> Download Images Backup</>
            )}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Backup;
